import { useEffect, useMemo, useRef, useState } from 'react';
import { DsInput } from '@/modules/internal-v2/ds';
import { useSalesPersonCandidates } from '@/api/user/user.hooks';
import { SalesPersonCandidate } from '@/api/user/user.types';
import './sales-person-picker.css';

interface SalesPersonPickerProps {
  /** Currently-selected sales-person code, or null when none. */
  value: number | null;
  /** Called when the user picks a candidate or clears the selection. */
  onChange: (value: number | null) => void;
  disabled?: boolean;
  /** Overrides the default "search by …" placeholder. */
  placeholder?: string;
  /** Replaces the empty-state copy shown when nothing is selected. */
  emptyLabel?: string;
  /** Extra class on the root for context-specific sizing. */
  className?: string;
}

/**
 * Searchable picker for `users.sales_person_code`, backed by the synced
 * `employees` table. Filters client-side across code / first / last / email
 * because the candidate list is small (a few dozen rows). Renders an input
 * that doubles as both search and the textual representation of the
 * selection — clearing it returns the field to null.
 */
export function SalesPersonPicker({
  value,
  onChange,
  disabled,
  placeholder,
  emptyLabel,
  className,
}: SalesPersonPickerProps) {
  const { data: candidates, isPending } = useSalesPersonCandidates();

  // Two states: the input's text (what the user is typing) and an `open`
  // flag for the dropdown. The input text mirrors the selected candidate's
  // label until the user starts typing.
  const selected = useMemo(
    () => candidates?.find((c) => c.salesPersonCode === value) ?? null,
    [candidates, value],
  );
  // While the dropdown is open we show the user's typed query; while closed
  // we derive the input value from the current selection so external `value`
  // changes are reflected without a setState-in-effect sync.
  const [typedQuery, setTypedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const query = open ? typedQuery : selected ? formatCandidate(selected) : '';
  const setQuery = setTypedQuery;
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const list = candidates ?? [];
    const term = query.trim().toLowerCase();
    if (!term || term === (selected ? formatCandidate(selected).toLowerCase() : '')) {
      return list;
    }
    return list.filter((c) => matchesQuery(c, term));
  }, [candidates, query, selected]);

  const handleSelect = (candidate: SalesPersonCandidate) => {
    onChange(candidate.salesPersonCode);
    setQuery(formatCandidate(candidate));
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setOpen(false);
  };

  const inputPlaceholder = isPending
    ? 'Lade Verkäufer…'
    : (placeholder ?? 'Suche nach Name, E-Mail oder SAP-Code');

  return (
    <div className={`sp-picker${className ? ` ${className}` : ''}`} ref={rootRef}>
      <div className="sp-picker-input">
        <DsInput
          value={query}
          placeholder={inputPlaceholder}
          disabled={disabled || isPending}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setTypedQuery(selected ? formatCandidate(selected) : '');
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        {value != null && !disabled && (
          <button
            type="button"
            className="sp-picker-clear"
            aria-label="Auswahl aufheben"
            onClick={handleClear}
          >
            ×
          </button>
        )}
      </div>
      {open && !disabled && (
        <div className="sp-picker-popover">
          {filtered.length === 0 ? (
            <div className="sp-picker-empty">
              {isPending
                ? 'Lädt…'
                : query.trim()
                  ? 'Keine Treffer.'
                  : (emptyLabel ?? 'Keine Verkäufer synchronisiert.')}
            </div>
          ) : (
            <ul className="sp-picker-list">
              {filtered.slice(0, 50).map((candidate) => {
                const isSelected =
                  candidate.salesPersonCode === value;
                return (
                  <li key={candidate.salesPersonCode}>
                    <button
                      type="button"
                      className={`sp-picker-item${isSelected ? ' is-selected' : ''}`}
                      onClick={() => handleSelect(candidate)}
                    >
                      <span className="sp-picker-name">
                        {candidate.firstName} {candidate.lastName}
                      </span>
                      <span className="sp-picker-meta">
                        <span className="sp-picker-code">
                          #{candidate.salesPersonCode}
                        </span>
                        {candidate.email && (
                          <span className="sp-picker-email">
                            {candidate.email}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
              {filtered.length > 50 && (
                <li className="sp-picker-overflow">
                  {filtered.length - 50} weitere Treffer — Suche verfeinern.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function formatCandidate(c: SalesPersonCandidate): string {
  return `${c.firstName} ${c.lastName} · #${c.salesPersonCode}`;
}

function matchesQuery(c: SalesPersonCandidate, term: string): boolean {
  if (String(c.salesPersonCode).includes(term)) return true;
  if (c.firstName.toLowerCase().includes(term)) return true;
  if (c.lastName.toLowerCase().includes(term)) return true;
  if (c.email && c.email.toLowerCase().includes(term)) return true;
  return false;
}
